import {
  Alert,
  Button,
  Flex,
  IconArchiveLine,
  IconButton,
  IconEditLine,
  IconRubricLine,
  IconWarningSolid,
  Pill,
  ToggleGroup,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import { createContext, useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AIService } from "../../../../services/AIService";
import { LearningOutcomesService } from "../../../../services/LearningOutcomesService";
import { MasteryLevelContext } from "../../../../utils/Context";
import { HighlightTokens } from "../../../../utils/HighlightTokens";
import { useNotification } from "../../../CustomHooks/NotificationProvider";
import ArchiveModal from "../../../LearningOutcomes/ArchiveModal";
import {
  getCurrentVersion,
  getFutureVersions,
  hasUnmappedRatingScale,
  isArchived,
  pendingArchive,
} from "../../../LearningOutcomes/utils";
import EditOutcomeModal from "../EditOutcomeModal";
import { OutcomesContext } from "../Review";
import { AiStatement, AiStatementButton } from "./AIStatement";
import { OutcomeDetails } from "./OutcomeDetails";
import OutcomeMastery from "./OutcomeMastery";

export const RefreshContext = createContext();

const ApprovedOutcomeToggle = ({
  outcome,
  offering,
  refreshLOs,
  archived,
  handleArchiveOutcome,
}) => {
  const showNotification = useNotification();
  const { ratingScales } = useContext(MasteryLevelContext);
  const { qcEnabled, qcConfig } = useContext(OutcomesContext);
  const [expanded, setExpanded] = useState(false);
  const [currentOutcome, setCurrentOutcome] = useState(
    getCurrentVersion(outcome)
  );
  const [tokenLevels, setTokenLevels] = useState([]);
  const [aiOutcomeStatementsPending, setAiOutcomeStatementsPending] =
    useState(false);
  const [aiOutcomeResponse, setAiOutcomeResponse] = useState(null);

  useEffect(() => {
    outcome.statement = outcome.statement.replace(/<[^>]*>/g, "");
    const tokens = [];

    outcome.demonstrated_skill_levels.forEach((level) => {
      level.tokens_demonstrating_skill_level.forEach((token) => {
        tokens.push(token.identified_cognate);
      });
    });
    setTokenLevels(tokens);
  }, [outcome]);

  const handleSaveOutcome = async (newName, newStatement) => {
    try {
      const newOutcome = await LearningOutcomesService.updateLearningOutcome({
        id: outcome.id,
        name: newName,
        seq: outcome.sequence,
        statement: newStatement,
        outcomeType: outcome.outcome_type,
      });
      setCurrentOutcome(newOutcome);
      showNotification(
        t("notifications.general.updateSuccess", { obj: t("ratingScale.lo") })
      );
    } catch {
      throw new Error(
        t("notifications.general.updateError", { obj: t("ratingScale.lo") })
      );
    }
  };

  const handleAiButton = () => {
    setAiOutcomeStatementsPending(true);
    setExpanded(true);
    AIService.getEnhancedOutcomeStatement(
      outcome.id,
      offering.course_summary.parent_course_id,
      offering.course_summary.representative_section_name,
      offering.code
    )
      .then((response) => setAiOutcomeResponse(response))
      .catch(() => {
        setAiOutcomeResponse({ error: t("analyze.aiError") });
      })
      .finally(() => setAiOutcomeStatementsPending(false));
  };

  return (
    <div
      style={{
        border: "1px solid #ccc",
        marginBottom: 15,
        borderRadius: "5px",
      }}
    >
      <ToggleGroup
        size="small"
        border={false}
        toggleLabel={`${t("analyze.showingResultsFor")} ${offering.code}`}
        summary={
          <Flex gap="small">
            <Flex.Item shouldShrink>
              <HighlightTokens
                title={currentOutcome.name}
                text={currentOutcome.statement}
                tokenLevels={tokenLevels}
                qcEnabled={qcEnabled}
              />
            </Flex.Item>
            {!archived && (
              <Flex.Item shouldGrow>
                <EditOutcomeButton
                  outcome={currentOutcome}
                  handleSave={handleSaveOutcome}
                />
              </Flex.Item>
            )}
            {/* Outcome is archived */}
            {isArchived(outcome) && (
              <Flex.Item shouldGrow textAlign="end">
                <Pill>
                  {t("learningOutcomes.archive.archived")}{" "}
                  {outcome.archive_term.insights_term_id}
                </Pill>
              </Flex.Item>
            )}
            {/* Outcome will be archived in the future */}
            {pendingArchive(outcome) && (
              <Pill>
                {t("learningOutcomes.archive.archiving")}{" "}
                {outcome.archive_term.insights_term_id}
              </Pill>
            )}
            {getFutureVersions(outcome).length > 0 && (
              <Flex.Item>
                <Pill color="info">{t("analyze.futureVersionExists")}</Pill>
              </Flex.Item>
            )}
            {qcEnabled && qcConfig.ai_enabled && (
              <Flex.Item>
                <AiStatementButton
                  handler={handleAiButton}
                  loading={aiOutcomeStatementsPending}
                />
              </Flex.Item>
            )}
            {outcome.warnings && outcome.warnings.length > 0 && qcEnabled && (
              <Flex.Item>
                <IconWarningSolid size="x-small" color="warning" />
              </Flex.Item>
            )}
            {(outcome.rating_scales.length === 0 ||
              hasUnmappedRatingScale(outcome, ratingScales)) && (
              <Flex.Item>
                <Tooltip
                  renderTip={
                    <div style={{ maxWidth: 450 }}>
                      {hasUnmappedRatingScale(outcome, ratingScales)
                        ? t("learningOutcomes.alignedToUnmapped")
                        : t("analyze.noRating")}
                    </div>
                  }
                  on={["hover", "focus"]}
                  color="primary-inverse"
                  placement="start"
                >
                  <IconRubricLine size="x-small" color="warning" />
                </Tooltip>
              </Flex.Item>
            )}
          </Flex>
        }
        iconPosition="start"
        fluidWidth
        expanded={expanded}
        onToggle={(_e, exp) => setExpanded(exp)}
      >
        <Flex wrap="wrap" padding="small">
          {qcEnabled &&
            qcConfig.ai_enabled &&
            (aiOutcomeStatementsPending ? (
              <Flex.Item size="100%">
                <Skeleton count={1} height={46} />
              </Flex.Item>
            ) : (
              <Flex.Item size="100%">
                {!!aiOutcomeResponse &&
                  (aiOutcomeResponse.error ? (
                    <Alert
                      variant="warning"
                      timeout={5000}
                      onDismiss={() => setAiOutcomeResponse(null)}
                    >
                      {aiOutcomeResponse.error}
                    </Alert>
                  ) : (
                    <AiStatement statement={aiOutcomeResponse} />
                  ))}
              </Flex.Item>
            ))}
          {qcEnabled && outcome.warnings && outcome.warnings.length > 0 && (
            <Flex.Item size="100%">
              <Alert variant="warning">
                <ul>
                  {outcome.warnings.map((warning, key) => (
                    <li key={key}>{warning}</li>
                  ))}
                </ul>
              </Alert>
            </Flex.Item>
          )}
          <Flex.Item shouldGrow padding={archived ? "xx-small 0" : "none"}>
            <OutcomeMastery outcome={currentOutcome} archived={archived} />
          </Flex.Item>
          {!archived && (
            <Flex.Item>
              <ArchiveButton
                outcome={currentOutcome}
                callback={handleArchiveOutcome}
              />
            </Flex.Item>
          )}
          <Flex.Item size="100%">
            <RefreshContext.Provider value={{ refreshLOs, archived }}>
              <OutcomeDetails outcome={outcome} />
            </RefreshContext.Provider>
          </Flex.Item>
        </Flex>
      </ToggleGroup>
    </div>
  );
};

const EditOutcomeButton = ({ outcome, handleSave }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip
        renderTip={t("analyze.editLearningOutcome")}
        color="primary-inverse"
      >
        <IconButton
          screenReaderLabel={t("analyze.editLearningOutcome")}
          size="small"
          onClick={() => setOpen(true)}
          renderIcon={IconEditLine}
        />
      </Tooltip>
      <EditOutcomeModal
        open={open}
        setOpen={setOpen}
        outcome={outcome}
        handleSave={handleSave}
      />
    </>
  );
};

const ArchiveButton = ({ outcome, callback }) => {
  const [open, setOpen] = useState(false);
  const disabled =
    getFutureVersions(outcome).length > 0 || pendingArchive(outcome);

  return (
    <>
      <Button
        disabled={disabled}
        color="primary"
        size="small"
        renderIcon={IconArchiveLine}
        onClick={() => setOpen(true)}
      >
        {t("learningOutcomes.archive.archive")}
      </Button>
      <ArchiveModal
        outcome={outcome}
        open={open}
        setOpen={setOpen}
        callback={callback}
      />
    </>
  );
};

export default ApprovedOutcomeToggle;
